/**
 * Request security utilities for idempotency and request signing
 *
 * Per-user signing: use signing_secret from user (login / users/info).
 * Message format: METHOD:path:timestamp:nonce:bodyHash
 * Replay protection: X-Request-Nonce (UUID per request)
 */

/**
 * Generate a unique UUID (for idempotency key or nonce)
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Alias for nonce generation (same as idempotency key) */
export function generateNonce(): string {
  return generateIdempotencyKey();
}

/**
 * Sensitive endpoints that require request signing (per-user secret)
 */
const SENSITIVE_ENDPOINTS: Array<{ pattern: RegExp; methods: string[] }> = [
  { pattern: /^\/api\/auth\/user\/update\/password$/, methods: ['PUT'] },
  { pattern: /^\/api\/user\/account\/password\/update$/, methods: ['PUT'] },
  { pattern: /^\/api\/user\/account\/username\/update$/, methods: ['PUT'] },
  { pattern: /^\/api\/user\/account\/delete$/, methods: ['PUT'] },
  { pattern: /^\/api\/user\/account\/sessions\/terminate-all$/, methods: ['POST'] },
  { pattern: /^\/api\/user\/account\/signing-secret\/regenerate$/, methods: ['POST'] },
  { pattern: /^\/api\/payment\/voucher$/, methods: ['POST'] },
  { pattern: /^\/api\/payment\/[^/]+$/, methods: ['PUT', 'DELETE'] },
  { pattern: /^\/api\/payment$/, methods: ['POST'] },
];

export function requiresSigning(method: string, path: string): boolean {
  const pathWithoutQuery = path.split('?')[0];
  for (const { pattern, methods } of SENSITIVE_ENDPOINTS) {
    if (pattern.test(pathWithoutQuery) && methods.includes(method.toUpperCase())) {
      return true;
    }
  }
  return false;
}

async function sha256(data: string): Promise<string> {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  if (typeof require !== 'undefined') {
    try {
      const crypto = require('crypto');
      return crypto.createHash('sha256').update(data).digest('hex');
    } catch {
      /* noop */
    }
  }
  throw new Error('SHA-256 not available in this environment');
}

async function hmacSha256(secret: string, message: string): Promise<string> {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await window.crypto.subtle.sign('HMAC', key, messageData);
    const signatureArray = Array.from(new Uint8Array(signature));
    return signatureArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  if (typeof require !== 'undefined') {
    try {
      const crypto = require('crypto');
      return crypto.createHmac('sha256', secret).update(message).digest('hex');
    } catch {
      /* noop */
    }
  }
  throw new Error('HMAC-SHA256 not available in this environment');
}

export interface SignRequestResult {
  signature: string;
  timestamp: string;
  nonce: string;
}

/**
 * Sign a request using HMAC-SHA256 (per-user secret).
 * Message format: METHOD:path:timestamp:nonce:bodyHash
 */
export async function signRequest(
  method: string,
  path: string,
  timestamp: number,
  nonce: string,
  body: string | null,
  secret: string
): Promise<SignRequestResult> {
  const pathWithoutQuery = path.split('?')[0];
  const bodyHash = await sha256(body || '');
  const message = `${method.toUpperCase()}:${pathWithoutQuery}:${timestamp}:${nonce}:${bodyHash}`;
  const signature = await hmacSha256(secret, message);
  return {
    signature,
    timestamp: String(timestamp),
    nonce,
  };
}

export function shouldUseIdempotency(method: string): boolean {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}
